/**
 * Note Text Backups Admin Component
 * 
 * Allows admins to search and view text snapshots of notes
 * for recovery purposes. Backups are retained for 30 days.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, FileText, ChevronUp, ChevronDown, Copy, Loader, Maximize2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Backups store naive UTC timestamps (no tz suffix); without the 'Z' the
// browser would parse them as LOCAL time and display UTC's clock reading.
const fmtBackupTime = (v) => {
  if (!v) return '';
  let s = String(v);
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d) ? String(v) : d.toLocaleString();
};

// DOS is a calendar date — format the raw date parts, never tz-shift it.
const fmtDos = (v) => {
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split('-');
  return (y && m && d) ? `${m}/${d}/${y}` : s;
};

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
  toast.success('Text copied to clipboard');
};

/**
 * Large-window backup browser (Jun 2026) — opened from the card's expand
 * button. Adds days-back / form / client / provider filters on top of the
 * server-side text search. The inline card below is unchanged.
 */
const NoteTextBackupsDialog = ({ open, onOpenChange }) => {
  const [q, setQ] = useState('');
  const [daysBack, setDaysBack] = useState('90');
  const [formFilter, setFormFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const fetchRows = useCallback(async (searchText, days, clientId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/note-text-backups`, {
        params: {
          search: searchText || undefined,
          days_back: Number(days) || 90,
          limit: 200,
          client_id: clientId && clientId !== 'all' ? clientId : undefined,
        },
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      setRows(response.data.backups || []);
    } catch (err) {
      toast.error('Failed to load text backups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchRows(q, daysBack, clientFilter);
    // eslint-disable-next-line
  }, [open, daysBack, clientFilter]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/admin/note-text-backups/clients`, {
      headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
    }).then((r) => setAllClients(r.data.clients || [])).catch(() => setAllClients([]));
  }, [open]);

  const formOptions = useMemo(() => [...new Set(rows.map((r) => r.template_name).filter(Boolean))].sort(), [rows]);
  const providerOptions = useMemo(() => [...new Set(rows.map((r) => r.provider_name).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => rows.filter((r) => (
    (formFilter === 'all' || r.template_name === formFilter)
    && (providerFilter === 'all' || r.provider_name === providerFilter)
  )), [rows, formFilter, providerFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[85vh] flex flex-col" data-testid="note-backups-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" /> Note Text Backups
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex gap-2 flex-1 min-w-[240px]">
            <Input
              placeholder="Search text, client, provider, or form..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchRows(q, daysBack, clientFilter); }}
              data-testid="backups-dialog-search-input"
            />
            <Button size="sm" onClick={() => fetchRows(q, daysBack, clientFilter)} disabled={loading} data-testid="backups-dialog-search-btn">
              {loading ? <Loader className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            </Button>
          </div>
          <Select value={daysBack} onValueChange={setDaysBack}>
            <SelectTrigger className="w-32" data-testid="backups-dialog-days-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
              <SelectItem value="3650">All time</SelectItem>
            </SelectContent>
          </Select>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-44" data-testid="backups-dialog-client-filter"><SelectValue placeholder="All clients" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              {allClients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={providerFilter} onValueChange={setProviderFilter}>
            <SelectTrigger className="w-44" data-testid="backups-dialog-provider-filter"><SelectValue placeholder="All providers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All providers</SelectItem>
              {providerOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={formFilter} onValueChange={setFormFilter}>
            <SelectTrigger className="w-44" data-testid="backups-dialog-form-filter"><SelectValue placeholder="All forms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All forms</SelectItem>
              {formOptions.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-gray-500">{filtered.length} of {rows.length} backup(s)</p>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1" data-testid="backups-dialog-results">
          {filtered.map((backup, idx) => (
            <div key={backup.id || idx} className="border rounded-lg p-3 text-sm bg-white">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium">{backup.client_name || 'Unknown Client'}</div>
                  <div className="text-xs text-gray-500">
                    {backup.template_name} • {backup.provider_name}
                    {backup.status && <span className="ml-1 uppercase text-[10px] text-gray-400">[{backup.status}]</span>}
                  </div>
                  <div className="text-xs text-gray-400">
                    Captured: {fmtBackupTime(backup.created_at)}
                    {backup.date_of_service && <span> · DOS: {fmtDos(backup.date_of_service)}</span>}
                  </div>
                </div>
                <Button size="sm" variant="ghost" data-testid={`backups-dialog-expand-${idx}`}
                  onClick={() => setExpandedId(expandedId === backup.id ? null : backup.id)}>
                  {expandedId === backup.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </div>
              {expandedId === backup.id && (
                <div className="mt-2 pt-2 border-t">
                  <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-3 rounded max-h-[45vh] overflow-y-auto font-mono">
                    {backup.text_content}
                  </pre>
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(backup.text_content)}>
                      <Copy className="h-3 w-3 mr-1" /> Copy Text
                    </Button>
                    <span className="text-xs text-gray-400 self-center">Note ID: {backup.note_id}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">No backups match the current filters.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const NoteTextBackups = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [showLarge, setShowLarge] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/admin/note-text-backups`, {
        params: { search: searchTerm, limit: 50 },
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
      });
      setResults(response.data.backups || []);
      if (response.data.backups?.length === 0) {
        toast.info('No text backups found matching your search');
      }
    } catch (err) {
      toast.error('Failed to search text backups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            Note Text Backups
          </span>
          <Button size="sm" variant="ghost" title="Open in a larger window with filters"
            onClick={() => setShowLarge(true)} data-testid="open-backups-dialog-btn">
            <Maximize2 className="h-4 w-4" />
          </Button>
        </CardTitle>
        <p className="text-sm text-gray-500">Search text snapshots of notes (30-day retention)</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="Search by text, client, or provider..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
          />
          <Button
            onClick={handleSearch}
            disabled={loading}
            size="sm"
          >
            {loading ? <Loader className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </div>
        
        {results.length > 0 && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            <p className="text-sm text-gray-500">{results.length} backup(s) found</p>
            {results.map((backup, idx) => (
              <div key={backup.id || idx} className="border rounded-lg p-3 text-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{backup.client_name || 'Unknown Client'}</div>
                    <div className="text-xs text-gray-500">
                      {backup.template_name || 'Unknown Form'} • {backup.provider_name || 'Unknown Provider'}
                    </div>
                    <div className="text-xs text-gray-400" data-testid={`backup-timestamp-${idx}`}>
                      Captured: {fmtBackupTime(backup.created_at)}
                      {backup.date_of_service && (
                        <span data-testid={`backup-dos-${idx}`}> · DOS: {fmtDos(backup.date_of_service)}</span>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setExpandedId(expandedId === backup.id ? null : backup.id)}
                  >
                    {expandedId === backup.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                </div>
                {expandedId === backup.id && (
                  <div className="mt-2 pt-2 border-t">
                    <pre className="whitespace-pre-wrap text-xs bg-gray-50 p-2 rounded max-h-60 overflow-y-auto font-mono">
                      {backup.text_content}
                    </pre>
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(backup.text_content)}
                      >
                        <Copy className="h-3 w-3 mr-1" /> Copy Text
                      </Button>
                      <span className="text-xs text-gray-400 self-center">
                        Note ID: {backup.note_id}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <NoteTextBackupsDialog open={showLarge} onOpenChange={setShowLarge} />
    </Card>
  );
};

export default NoteTextBackups;
